import utils from './utils'

const UTC_DAY_DELTA_TIMESTAMP = 24 * 60 * 60 * 1000

function formatAsTwoDigitString(i: number): string {
  if (Math.floor(i) !== i || i < 0 || i > 99) {
    throw new Error(`Cannot format ${i}`);
  }
  return (i < 10 ? "0" : "") + i
}

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

export class ISODate {
  readonly year: number;
  readonly month: number
  readonly date: number

  // make sure to return the same object for the same date
  static createdDateMap: Map<number, ISODate> = new Map()

  constructor(year: number, month: number, date: number) {
    const d = new Date(year, month - 1, date)
    utils.assert (d.getFullYear() === year
            && d.getMonth() + 1 === month
            && d.getDate() === date,
            `Invalid date: ${year}-${month}-${date}`)
    this.date = date
    this.month = month
    this.year = year

    const key = this.getOrdinal()
    if (!ISODate.createdDateMap.has(key)) {
      ISODate.createdDateMap.set(key, this)
    }
    return ISODate.createdDateMap.get(key)!
  }

  getOrdinal(): number {
    return this.toUTCNativeDate().valueOf() / MILLISECONDS_PER_DAY
  }

  toString(): string {
    return this.toISOString()
  }

  toISOString(): string {
    return [this.year.toString(),
            formatAsTwoDigitString(this.month),
            formatAsTwoDigitString(this.date)].join("-")
  }

  toDDMMYYYYString(): string {
    return [formatAsTwoDigitString(this.date),
            formatAsTwoDigitString(this.month),
            this.year.toString()].join("-")
  }

  weekDayName(locale?: string): string {
    return this.toUTCNativeDate().toLocaleDateString(
      locale ?? "en-UK", {weekday: "long"})
  }

  monthName(locale?: string): string {
    return this.toUTCNativeDate().toLocaleDateString(
      locale ?? "en-UK", {month: "long"})
  }

  getDaysUntil(date: ISODate): number {
    return date.getOrdinal() - this.getOrdinal()
  }

  toISOWeekString(): string {
    const date = this.toLocalNativeDate()
    // from https://weeknumber.com/how-to/javascript

    // Thursday in current week decides the year.
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    // January 4 is always in week 1.
    var week1 = new Date(date.getFullYear(), 0, 4);
    // Adjust to Thursday in week 1 and count number of weeks from date to week1.
    const weeknr = 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000
                                   - 3 + (week1.getDay() + 6) % 7) / 7);
    return "W" + formatAsTwoDigitString(weeknr) + "/" + this.year.toString()
  }

  static parseISO(isodatestring: string): ISODate {
    try {
      const [year, month, date] = isodatestring.split("-").map(a => parseInt(a))
      return new ISODate(year, month, date)
    } catch(e) {
      throw new Error("Cannot parse '" + isodatestring + "' as isodate: " + e)
    }
  }

  static fromLocalNativeDate(date: Date): ISODate {
    utils.assert(date.getHours() === 0
           && date.getMinutes() === 0
           && date.getSeconds() === 0
           && date.getMilliseconds() === 0,
           "Date is not midnight in local: " + date.toString())
    return new ISODate(date.getFullYear(), date.getMonth() + 1, date.getDate())
  }

  static fromUTCNativeDate(date: Date): ISODate {
    utils.assert(date.getUTCHours() === 0
           && date.getUTCMinutes() === 0
           && date.getUTCSeconds() === 0
           && date.getUTCMilliseconds() === 0,
           "Date is not midnight in local: " + date.toISOString())
    return new ISODate(
      date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
  }

  toLocalNativeDate(): Date {
    return new Date(this.year, this.month - 1, this.date)
  }

  toUTCNativeDate(): Date {
    return new Date(Date.UTC(this.year, this.month - 1, this.date))
  }

  lt(other: ISODate): boolean {
    return ISODate.compare(this, other) < 0
  }

  gt(other: ISODate): boolean {
    return ISODate.compare(this, other) > 0
  }

  le(other: ISODate): boolean {
    return ISODate.compare(this, other) <= 0
  }

  ge(other: ISODate): boolean {
    return ISODate.compare(this, other) >= 0
  }

  add(days: number): ISODate {
    const utctimestamp = this.toUTCNativeDate().valueOf()
      + days * UTC_DAY_DELTA_TIMESTAMP
    return ISODate.fromUTCNativeDate(new Date(utctimestamp))
  }

  static localToday() {
    const now = new Date()
    return new ISODate(now.getFullYear(), now.getMonth() + 1, now.getDate())
  }

  static utcToday() {
    const now = new Date()
    return new ISODate(
      now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate())
  }

  static compare(a: ISODate, b: ISODate): number {
    return (a.year - b.year) * 10000
      + (a.month - b.month) * 100
      + (a.date - b.date)
  }

  static earliest(items: never[]): undefined;
  static earliest(items: ISODate[]): ISODate;
  static earliest(items: ISODate[]): ISODate | undefined {
    if (items.length === 0) {
      return undefined
    }
    return items.slice(1).reduce(
      (a: ISODate, b: ISODate) => this.compare(a, b) < 0 ? a : b,
      items[0]
    )
  }

  static latest(items: never[]): undefined;
  static latest(items: ISODate[]): ISODate;
  static latest(items: ISODate[]): ISODate | undefined {
    if (items.length === 0) {
      return undefined
    }
    return items.slice(1).reduce(
      (a: ISODate, b: ISODate) => this.compare(a, b) > 0 ? a : b,
      items[0]
    )
  }
}
